import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Chip, DChip, Icon } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useShelf } from '../stock-manager/lib/useStock';
import { errorText, useTechActions, useWorklist, type TechTask } from './api';
import { ageSex, drName, hintLine, longDay, mmss, secondsUntil, shortName, useNow } from './format';
import { buildItems, countLanes, isClosed, LANE_LABEL, type Item, type LaneFilter } from './model';
import { WorklistPane, useOpenTest } from './components/WorklistPane';
import { CantModal } from './components/CantModal';
import { PhoneScreen } from './components/PhoneScreen';
import { useTechOverlays } from './components/TechOverlays';

const LANES: LaneFilter[] = ['all', 'ready', 'waiting', 'with_you', 'done', 'tomorrow'];
const PAGE = 20;

/**
 * Technician · Worklist — `g_w`, `g_wempty`, phone `g_t1`.
 *
 * SEQUENCED BY READINESS, not by order time. Every test sits in exactly one
 * lane and the lane rule is printed under the table. A bundle — one patient's
 * tests from the same visit — is one row, and docks as its sequence.
 */
export function TechWorklistPage() {
  const navigate = useNavigate();
  const { branch } = useShellIdentity();
  const branchName = branch.replace(/\s*▾\s*$/, '');
  const list = useWorklist();
  const act = useTechActions();
  const open = useOpenTest();
  const [lane, setLane] = useState<LaneFilter>('all');
  /* A bundle opened on the phone (or a row picked elsewhere) arrives docked. */
  const location = useLocation();
  const [selectedId, setSelectedId] = useState<string | null>(
    (location.state as { select?: string } | null)?.select ?? null,
  );
  const [dropped, setDropped] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const [cant, setCant] = useState<TechTask | null>(null);
  usePageMeta({ title: 'Worklist' });
  const ov = useTechOverlays();

  const allItems = useMemo(() => buildItems(list.data?.tasks ?? []), [list.data]);
  const counts = countLanes(allItems);
  const items = useMemo(
    () => allItems.filter((i) => (lane === 'all' ? i.lane !== 'tomorrow' : i.lane === lane)),
    [allItems, lane],
  );
  const ticking = items.some((i) => i.tasks.some((t) => t.waiting?.until));
  const now = useNow(ticking);

  /* The selection follows the rows on screen: the picked one, else the first —
     unless a lane click just dropped it, which the pane says. */
  const selected: Item | null = items.find((i) => i.id === selectedId || i.tasks.some((t) => t.id === selectedId)) ?? (dropped ? null : items[0] ?? null);
  const pickLane = (l: LaneFilter) => {
    setLane(l);
    setShown(PAGE);
    if (selectedId || selected) {
      setSelectedId(null);
      setDropped(true);
    }
  };
  const select = (id: string) => {
    setSelectedId(id);
    setDropped(false);
  };
  /* Loaded and empty — not merely loading. A first paint of "Nothing ordered"
     that then fills in reads as a false all-clear. */
  const empty = Boolean(list.data) && allItems.length === 0;
  const backToToday = () => {
    setLane('all');
    setDropped(false);
  };

  const sig = selected
    ? `${selected.id}:${selected.tasks.map((t) => `${t.id}.${t.state}.${t.lane}.${t.arrived}.${t.device?.status ?? '-'}`).join(',')}`
    : `none:${dropped}`;
  const empties = empty ? 'empty' : 'rows';

  useShellChrome(
    {
      raw: true,
      title: 'Investigations',
      sub: `${branchName} · ${empty ? 'nothing ordered yet' : longDay()}`,
      search: 'Search orders, patients, MRN…',
      onSearch: ov.openSearch,
      onBell: ov.openBell,
      primary: empty
        ? { label: 'Back to today', onClick: backToToday }
        : { label: 'Hand to', onClick: () => navigate('/technician/worklist/hand-over') },
      pane: <WorklistPane item={empty ? null : selected} />,
      phone: <PhoneWorklist />,
    },
    [branchName, sig, empties],
  );

  const rows = items.slice(0, shown);
  const lowStock = (useShelf().data ?? []).filter((i) => i.low).length;

  return (
    <div className="maie desk tech-root">
        {ov.overlays}
      <div className="filterrow">
        {empty ? (
          <>
            <Chip kind="brand" small className="w6">All · 0</Chip>
            <Chip kind="outline" small onClick={backToToday}>Back to today</Chip>
          </>
        ) : (
          LANES.map((l) => (
            <Chip key={l} small kind={lane === l ? 'brand' : 'outline'} className={lane === l ? 'w6' : undefined}
              onClick={() => pickLane(l)}>
              {LANE_LABEL[l]} · {counts[l]}
            </Chip>
          ))
        )}
        {lowStock ? (
          <>
            <span className="grow" />
            <DChip kind="warn" onClick={() => navigate('/technician/stock')}>
              <Icon name="alert-triangle" className="ic-inline" /> Low stock · {lowStock}
            </DChip>
          </>
        ) : null}
      </div>
      <div className="dcontent">
        {!list.data ? null : empty || items.length === 0 ? (
          <>
            <div className="emptystate">
              <p className="es-t">Nothing ordered yet — the doctors will fill this.</p>
              <p className="mt12"><DChip kind="outline" onClick={backToToday}>Back to today</DChip></p>
            </div>
          </>
        ) : (
          <>
            <table className="dtable">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Test</th>
                  <th style={{ width: '21%' }}>Patient</th>
                  <th style={{ width: '11%' }}>Takes</th>
                  <th style={{ width: '16%' }}>Ready?</th>
                  <th style={{ width: '12%' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <WorklistRow
                    key={i.id}
                    item={i}
                    now={now}
                    selected={selected?.id === i.id}
                    onSelect={() => select(i.id)}
                    onStart={() => open(i.next)}
                    onCant={() => setCant(i.next)}
                    onCall={() => act.call.mutate(i.next.id, {
                      onSuccess: () => toast.success(`The desk is calling ${shortName(i.next.patient) || 'the patient'}`),
                      onError: (e) => toast.error(errorText(e)),
                    })}
                  />
                ))}
              </tbody>
            </table>
            {items.length > shown ? (
              <div className="dt-more">
                showing {shown} of {items.length} · <button type="button" className="lnk" onClick={() => setShown((n) => n + PAGE)}>Load more</button>
              </div>
            ) : null}
            <p className="t-sec mut mt12">Orders publish the moment a doctor signs — or mid-visit, flagged urgent.</p>
          </>
        )}
      </div>
      {cant ? <CantModal task={cant} open onClose={() => setCant(null)} /> : null}
    </div>
  );
}

function rowTitle(i: Item) {
  const t = i.next;
  if (i.bundle) return `${shortName(t.patient)} — bundle of ${i.tasks.length}`;
  return t.title;
}

function patientCell(i: Item) {
  const t = i.next;
  const who = shortName(t.patient);
  const age = t.priority !== 'routine' || t.testOnly ? ageSex(t.patient) : '';
  const dr = t.testOnly ? '' : drName(t.requestedBy);
  return [who, age, dr].filter(Boolean).join(' · ');
}

function takes(i: Item) {
  const m = i.tasks.reduce((s, t) => s + (t.takesMin ?? 0), 0);
  return m ? `${m} min` : '—';
}

export function ReadyChip({ item, now, onCall }: { item: Item; now: number; onCall?: () => void }) {
  const t = item.next;
  if (item.lane === 'done') return <span className="t-sec sec">—</span>;
  if (item.lane === 'with_you') return <Chip kind="soft" small>in progress · you</Chip>;
  if (item.lane === 'waiting') {
    const left = secondsUntil(t.waiting?.until, now);
    return (
      <Chip kind="warn" small>
        <Icon name="clock" size="sm" /> {t.waiting?.label}{left != null ? ` ${mmss(left)}` : ''}
      </Chip>
    );
  }
  if (item.lane === 'tomorrow') return <Chip kind="outline" small>tomorrow</Chip>;
  if (t.testOnly) return <Chip kind="warn" small>test-only</Chip>;
  if (t.arrived === false) {
    return (
      <>
        <Chip kind="warn" small>not in room</Chip>{' '}
        {onCall ? (
          <DChip kind="outline" onClick={onCall}><Icon name="speakerphone" size="sm" /> Call</DChip>
        ) : null}
      </>
    );
  }
  if (t.arrived) return <Chip kind="success" small>here ✓</Chip>;
  return <span className="t-sec sec">—</span>;
}

function WorklistRow({ item, now, selected, onSelect, onStart, onCant, onCall }: {
  item: Item;
  now: number;
  selected: boolean;
  onSelect: () => void;
  onStart: () => void;
  onCant: () => void;
  onCall: () => void;
}) {
  const t = item.next;
  const hint = item.bundle
    ? { text: `${item.tasks.map((x) => x.kindLabel === 'Test' ? x.title : x.kindLabel).join(' · ')} — one sitting`, tone: 'mut' as const }
    : hintLine(t);
  const toneClass = hint ? { mut: 't-cap mut', brand: 't-cap', red: 't-cap red', amb: 't-cap amb' }[hint.tone] : '';
  const closed = item.tasks.every(isClosed);
  return (
    <tr className={`rowc${selected ? ' sel' : ''}`} onClick={onSelect}>
      <td>
        <p className="t-body w6">
          {rowTitle(item)}
          {!item.bundle && t.priority !== 'routine' && !closed ? <> <Chip kind="danger" small>{t.priority}</Chip></> : null}
          {!item.bundle && t.repeatOfId && !closed ? <> <Chip kind="danger" small>repeat</Chip></> : null}
        </p>
        {hint ? (
          <p className={toneClass} style={hint.tone === 'brand' ? { color: 'var(--brand-600)' } : undefined}>{hint.text}</p>
        ) : null}
      </td>
      <td><span className="t-sec sec">{patientCell(item)}</span></td>
      <td><span className="t-sec sec" style={{ whiteSpace: 'nowrap' }}>{takes(item)}</span></td>
      <td onClick={(e) => e.stopPropagation()}><ReadyChip item={item} now={now} onCall={onCall} /></td>
      <td onClick={(e) => e.stopPropagation()}>
        {item.lane === 'done' ? (
          t.state === 'cant' && !item.bundle ? <Chip kind="warn" small>came back</Chip> : <Chip kind="success" small>done ✓</Chip>
        ) : item.lane === 'waiting' ? (
          <DChip kind="outline" onClick={onCant}>Can’t</DChip>
        ) : item.lane === 'with_you' ? null : item.lane === 'tomorrow' ? null : (
          <DChip kind="soft" onClick={onStart}>{t.repeatOfId ? 'Redo' : 'Start'}</DChip>
        )}
      </td>
    </tr>
  );
}

/* ─────────────────────────── phone · g_t1 ─────────────────────────── */

function PhoneWorklist() {
  const navigate = useNavigate();
  const { branch } = useShellIdentity();
  const branchName = branch.replace(/\s*▾\s*$/, '');
  const list = useWorklist();
  const act = useTechActions();
  const open = useOpenTest();
  const [lane, setLane] = useState<LaneFilter>('all');
  const [picker, setPicker] = useState(false);
  const allItems = useMemo(() => buildItems(list.data?.tasks ?? []), [list.data]);
  const counts = countLanes(allItems);
  const cantPage = (t: TechTask) => navigate(`/technician/worklist/${t.id}/cant`);
  const items = allItems.filter((i) => (lane === 'all' ? i.lane !== 'tomorrow' : i.lane === lane));
  const now = useNow(items.some((i) => i.tasks.some((t) => t.waiting?.until)));

  /* A card that has not been started is opened to look at, never started by
     the tap: starting runs the clock, so only the Start chip does it. */
  const [peek, setPeek] = useState<string | null>(null);
  const tap = (i: Item) => {
    if (i.bundle) navigate(`/technician/bundle/${i.next.id}`);
    else if (i.lane === 'waiting') cantPage(i.next);
    else if (i.next.state === 'open') setPeek((p) => (p === i.id ? null : i.id));
    else open(i.next);
  };

  return (
    <PhoneScreen
      door="worklist"
      title="Investigations"
      sub={branchName}
      trailing={(
        <div className="chiprow" style={{ marginRight: -4, display: 'flex' }}>
          <Chip kind="outline" small className="chip-act w6" onClick={() => navigate('/technician/worklist/hand-over')}>
            Hand to
          </Chip>
          <span style={{ width: 6 }} />
          <Chip kind="outline" small className="chip-act" onClick={() => setPicker((v) => !v)}>
            {lane === 'all' ? 'All' : LANE_LABEL[lane]} ▾
          </Chip>
        </div>
      )}
    >
      {picker ? (
        <div className="card">
          {(['all', 'ready', 'waiting', 'with_you', 'done', 'tomorrow'] as LaneFilter[]).map((l) => (
            <button key={l} type="button" className="card-btn" style={{ padding: '8px 0', background: 'none', border: 'none' }}
              onClick={() => {
                setLane(l);
                setPicker(false);
              }}>
              <span className={`t-body${lane === l ? ' w6 brand' : ''}`}>{LANE_LABEL[l]} · {counts[l]}</span>
            </button>
          ))}
        </div>
      ) : null}
      {items.map((i) => {
        const t = i.next;
        const hint = i.bundle ? null : hintLine(t);
        const border = t.priority !== 'routine' || t.repeatOfId
          ? 'var(--danger-600)'
          : i.bundle || i.lane === 'with_you' ? 'var(--brand-300)'
            : t.testOnly ? 'var(--beige-500)' : undefined;
        const minutes = i.tasks.reduce((s, x) => s + (x.takesMin ?? 0), 0);
        const left = secondsUntil(t.waiting?.until, now);
        return (
          <div key={i.id} className="card" style={border ? { borderColor: border } : undefined}>
            <div className="flex" style={{ gap: 8 }}>
              <div className="grow">
              <button type="button" className="w-full text-start card-btn" style={{ background: 'none', border: 'none', padding: 0 }} onClick={() => tap(i)}>
                <p className="t-strong">
                  {rowTitle(i)}
                  {!i.bundle && t.priority !== 'routine' ? <> <Chip kind="danger" small>{t.priority}</Chip></> : null}
                  {!i.bundle && t.repeatOfId ? <> <Chip kind="danger" small>repeat</Chip></> : null}
                </p>
                {i.bundle ? (
                  <p className="t-sec mut">{i.tasks.map((x) => x.kindLabel === 'Test' ? x.title : x.kindLabel).join(' · ')} — one sitting</p>
                ) : t.repeatReason ? (
                  <p className="t-sec" style={{ color: 'var(--danger-600)' }}>{hint?.text}</p>
                ) : (
                  <p className="t-sec mut">
                    {[shortName(t.patient), t.priority !== 'routine' || t.testOnly ? ageSex(t.patient) : '', t.testOnly ? '' : drName(t.requestedBy)]
                      .filter(Boolean).join(' · ')}
                    {t.testOnly && hint ? <> · <span className="w6 ink">{hint.text}</span></> : null}
                    {!t.testOnly && t.priority !== 'routine' && hint?.tone === 'mut' ? ` — ${hint.text.replace(/ — visit open$/, '')}` : ''}
                  </p>
                )}
              </button>
                {i.lane === 'ready' && (!t.testOnly || i.bundle) && t.arrived != null ? (
                <p className="t-cap mut mt4">
                    {minutes ? `≈${minutes} min · ` : ''}
                    {t.arrived ? <span className="w6 grn">patient here ✓</span> : <span className="w6 amb">not in the room yet</span>}
                    {t.arrived === false ? <> <Chip kind="outline" small className="chip-act" onClick={() => act.call.mutate(t.id, {
                      onSuccess: () => toast.success(`The desk is calling ${shortName(t.patient) || 'the patient'}`),
                      onError: (e) => toast.error(errorText(e)),
                    })}><Icon name="speakerphone" size="sm" /> Call</Chip></> : null}
                  </p>
                ) : null}
              </div>
              {i.lane === 'ready' ? (
                t.testOnly && !i.bundle ? <Chip kind="warn" small>test-only</Chip>
                  : <Chip kind="soft" small className="chip-act w6" onClick={() => (i.bundle ? navigate(`/technician/bundle/${t.id}`) : open(t))}>{t.repeatOfId ? 'Redo' : 'Start'}</Chip>
              ) : i.lane === 'waiting' ? (
                <Chip kind="warn" small><Icon name="clock" size="sm" /> {t.waiting?.label}{left != null ? ` · ${mmss(left)}` : ''}</Chip>
              ) : i.lane === 'with_you' ? (
                <Chip kind="soft" small className="w6">in progress · you</Chip>
              ) : i.lane === 'done' ? (
                <Chip kind="success" small>done ✓ → review</Chip>
              ) : <Chip kind="outline" small>tomorrow</Chip>}
            </div>
            {!i.bundle && t.flow && t.state === 'open' ? (
              <p className="t-cap mt4" style={{ color: 'var(--brand-600)' }}>
                {hintLine(t)?.text.replace(' · then ', ' · then: ')}
              </p>
            ) : null}
            {i.lane === 'waiting' || (peek === i.id && !i.bundle) ? (
              <p className="mt8"><Chip kind="outline" small className="chip-act" onClick={() => cantPage(t)}>Can’t — reason ▾</Chip></p>
            ) : null}
          </div>
        );
      })}
      <p className="t-sec mut center mt12">Orders publish the moment a doctor signs — or mid-visit, flagged urgent</p>
      {items.length === 0 ? (
        <div className="card card-ghost center mt12">
          <p className="t-sec mut">Nothing ordered yet — the doctors will fill this.</p>
        </div>
      ) : null}
    </PhoneScreen>
  );
}
